import React, { useState, useEffect, useRef } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';

import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { API_URLS } from '../../Services/servicioURL';

interface BitacoraType {
  id: number;
  cve_producto: string;
  sucursal: string;
  departamento: string;
  observacion: string;
  fecha_registro: string; // ISO string desde backend
}

const Bitacora: React.FC = () => {
  const [formData, setFormData] = useState<BitacoraType>({
    id: 0,
    cve_producto: '',
    sucursal: '',
    departamento: '',
    observacion: '',
    fecha_registro: '',
  });
  const [mode, setMode] = useState<'view' | 'new' | 'edit'>('view');
  const [loading, setLoading] = useState<boolean>(true);
  const [mostrarTabla, setMostrarTabla] = useState<boolean>(false);
  const [bitacoras, setBitacoras] = useState<BitacoraType[]>([]);
  const [filteredBitacoras, setFilteredBitacoras] = useState<BitacoraType[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const toast = useRef<Toast>(null);
  const claveRef = useRef<HTMLInputElement>(null);

  // Cargar lista inicial al montar
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(API_URLS.bitacora.obtenerTodas);
        if (!response.ok) throw new Error('Error al cargar bitácoras');
        const data = await response.json();
        setBitacoras(data);
        setFilteredBitacoras(data);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error al cargar bitácoras', life: 2000 });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtrar bitácoras
  useEffect(() => {
    if (bitacoras.length > 0) {
      const lowercaseSearch = searchTerm.toLowerCase();
      setFilteredBitacoras(bitacoras.filter(b =>
        b.cve_producto.toLowerCase().includes(lowercaseSearch) ||
        b.sucursal.toLowerCase().includes(lowercaseSearch) ||
        b.departamento.toLowerCase().includes(lowercaseSearch) ||
        b.observacion.toLowerCase().includes(lowercaseSearch)
      ));
    } else {
      setFilteredBitacoras([]);
    }
  }, [searchTerm, bitacoras]);

  // Buscar bitácora por ID
  const buscarBitacoraPorId = async (id: number): Promise<BitacoraType | null> => {
    try {
      const response = await fetch(API_URLS.bitacora.obtenerPorId(id));
      return response.ok ? await response.json() : null;
    } catch (error) {
      console.error('Error al buscar bitácora:', error);
      return null;
    }
  };

  // Buscar producto para validar clave (opcional, pero útil)
  const validarProducto = async (cve: string): Promise<boolean> => {
    try {
      const response = await fetch(API_URLS.productos.obtenerPorCve(cve));
      return response.ok;
    } catch {
      return false;
    }
  };

  // Manejadores
  const handleCambios = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNuevo = () => {
    setFormData({
      id: 0,
      cve_producto: '',
      sucursal: '',
      departamento: '',
      observacion: '',
      fecha_registro: '',
    });
    setMode('new');
    setTimeout(() => claveRef.current?.focus(), 100);
  };

  const handleEditar = () => {
    if (!formData.id) {
      toast.current?.show({ severity: 'warn', summary: 'Advertencia', detail: 'Seleccione una bitácora para editar', life: 2000 });
      return;
    }
    setMode('edit');
  };

  const handleGuardar = async () => {
    const { cve_producto, sucursal, departamento } = formData;
    if (!cve_producto.trim() || !sucursal.trim() || !departamento.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Campos requeridos', detail: 'Clave, sucursal y departamento son obligatorios', life: 2000 });
      return;
    }

    // Validar que la clave de producto exista (opcional pero recomendado)
    const productoExiste = await validarProducto(cve_producto.trim());
    if (!productoExiste) {
      toast.current?.show({ severity: 'error', summary: 'Producto no válido', detail: 'La clave de producto no existe', life: 3000 });
      return;
    }

    try {
      const url = mode === 'new' ? API_URLS.bitacora.registrar : API_URLS.bitacora.actualizar(formData.id);
      const method = mode === 'new' ? 'POST' : 'PATCH';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) throw new Error('Error al guardar');

      setMode('view');
      toast.current?.show({
        severity: 'success',
        summary: '¡Éxito!',
        detail: mode === 'new' ? 'Bitácora registrada' : 'Bitácora actualizada',
        life: 2000
      });
    } catch (error) {
      console.error('Error:', error);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error al guardar la bitácora', life: 2000 });
    }
  };

  const handleCancelar = () => {
    setMode('view');
    setFormData({
      id: 0,
      cve_producto: '',
      sucursal: '',
      departamento: '',
      observacion: '',
      fecha_registro: '',
    });
  };

  const handleEliminar = async () => {
    if (!formData.id) {
      toast.current?.show({ severity: 'warn', summary: 'Advertencia', detail: 'Seleccione una bitácora para eliminar', life: 2000 });
      return;
    }

    const confirmacion = window.confirm('⚠️ ¿Está seguro de eliminar esta bitácora?\n\nEsta acción no se puede deshacer.');
    if (!confirmacion) return;

    try {
      const response = await fetch(API_URLS.bitacora.eliminar(formData.id), { method: 'DELETE' });
      if (!response.ok) throw new Error('Error al eliminar');

      toast.current?.show({ severity: 'success', summary: '¡Eliminado!', detail: 'Bitácora eliminada', life: 2000 });
      handleCancelar();
    } catch (error) {
      console.error('Error:', error);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error al eliminar la bitácora', life: 2000 });
    }
  };

  // Consulta
  const obtenerBitacoras = async () => {
    try {
      const response = await fetch(API_URLS.bitacora.obtenerTodas);
      if (!response.ok) throw new Error('Error al obtener bitácoras');
      const data = await response.json();
      setBitacoras(data);
      setFilteredBitacoras(data);
      setSearchTerm('');
    } catch (error) {
      console.error('Error:', error);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error al cargar bitácoras', life: 2000 });
    }
  };

  const handleBuscar = () => {
    obtenerBitacoras();
    setMostrarTabla(true);
  };

  const cerrarTabla = () => setMostrarTabla(false);

  const handleSeleccionarBitacora = (bitacora: BitacoraType) => {
    setFormData(bitacora);
    setMostrarTabla(false);
  };

  const getModeIndicator = () => {
    switch (mode) {
      case 'new': return { text: 'NUEVA BITÁCORA', color: '#10b981', icon: 'pi-plus' };
      case 'edit': return { text: 'EDITANDO BITÁCORA', color: '#f59e0b', icon: 'pi-pencil' };
      default: return { text: 'MODO CONSULTA', color: '#6b7280', icon: 'pi-eye' };
    }
  };

  const modeInfo = getModeIndicator();

  return (
    <>
      <Navbar />
      <Sidebar />
      <Toast ref={toast} />

      {/* Modal de consulta */}
      {mostrarTabla && (
        <div className="overlay" onClick={cerrarTabla} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px', maxWidth: '900px', width: '90%',
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                📜 Consultar Bitácoras
              </h3>
              <Button icon="pi pi-times" className="p-button-text p-button-rounded" onClick={cerrarTabla} style={{ color: '#6b7280' }} />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ position: 'relative' }}>
                <i className="pi pi-search" style={{
                  position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                  color: '#9ca3af', fontSize: '0.9rem'
                }}></i>
                <InputText
                  style={{ width: '100%', height: '40px', paddingLeft: '40px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '0.95rem' }}
                  placeholder="Buscar por clave de producto, sucursal, depto o observación..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div style={{ 
              marginBottom: '15px', fontSize: '0.9em', color: '#6b7280', padding: '8px 12px',
              backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb'
            }}>
              📊 Mostrando <strong>{filteredBitacoras.length}</strong> de <strong>{bitacoras.length}</strong> bitácoras
            </div>

            <div style={{ 
              flex: 1, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px',
              maxHeight: 'calc(80vh - 200px)'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>🔑 Clave Producto</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>🏢 Sucursal</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb' }}>🧍 Departamento</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBitacoras.length > 0 ? (
                    filteredBitacoras.map((b, index) => (
                      <tr 
                        key={b.id} 
                        onClick={() => handleSeleccionarBitacora(b)}
                        style={{
                          cursor: 'pointer', backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                      >
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{b.cve_producto}</td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{b.sucursal}</td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{b.departamento}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                        📭 No se encontraron bitácoras
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="content-wrapper" style={{
        backgroundImage: 'url(/dist/img/FondoLogin.png)', backgroundSize: 'cover', backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed', minHeight: '100vh', position: 'relative', padding: '20px'
      }}>
        
        {/* Header principal */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '16px', padding: '24px', marginBottom: '20px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h1 style={{ 
              margin: 0, color: '#1f2937', fontSize: '2rem', fontWeight: '700',
              display: 'flex', alignItems: 'center', gap: '12px'
            }}>
              📜 Bitácora de Productos
            </h1>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px',
              backgroundColor: modeInfo.color, color: 'white', borderRadius: '20px',
              fontSize: '0.85rem', fontWeight: '600'
            }}>
              <i className={`pi ${modeInfo.icon}`}></i>
              {modeInfo.text}
            </div>
          </div>
          <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
        </div>

        {/* Botones de acción */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', padding: '20px', marginBottom: '20px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {mode === 'view' ? (
              <>
                <Button icon="pi pi-search" label="Consultar" className="p-button-info p-button-raised" onClick={handleBuscar}
                  style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                <Button icon="pi pi-plus" label="Nueva" className="p-button-success p-button-raised" onClick={handleNuevo}
                  style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                <Button icon="pi pi-pencil" label="Editar" className="p-button-warning p-button-raised" onClick={handleEditar}
                  disabled={!formData.id} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px', opacity: !formData.id ? 0.5 : 1 }} />
                <Button icon="pi pi-trash" label="Eliminar" className="p-button-danger p-button-raised" onClick={handleEliminar}
                  disabled={!formData.id} style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px', opacity: !formData.id ? 0.5 : 1 }} />
              </>
            ) : (
              <>
                <Button icon="pi pi-save" label="Guardar" className="p-button-success p-button-raised" onClick={handleGuardar}
                  style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
                <Button icon="pi pi-times" label="Cancelar" className="p-button-secondary p-button-raised" onClick={handleCancelar}
                  style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }} />
              </>
            )}
          </div>
        </div>

        {/* Formulario principal */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', padding: '24px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <form onSubmit={(e) => e.preventDefault()} autoComplete="off">
            {/* Primera fila */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  🔢 ID
                </label>
                <InputText 
                value={formData.id ? formData.id.toString() : ''}
                disabled
                  style={{ width: '100%', height: '40px', fontSize: '0.95rem', padding: '0 12px', 
                    border: '2px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  🔑 Clave Producto <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <InputText 
                  ref={claveRef}
                  name="cve_producto"
                  value={formData.cve_producto}
                  onChange={handleCambios}
                  disabled={mode === 'view'}
                  placeholder="Clave del producto"
                  style={{ width: '100%', height: '40px', fontSize: '0.95rem', padding: '0 12px', 
                    border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`, borderRadius: '8px',
                    backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff' }}
                />
              </div>
            </div>

            {/* Segunda fila */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  🏢 Sucursal <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <InputText 
                  name="sucursal"
                  value={formData.sucursal}
                  onChange={handleCambios}
                  disabled={mode === 'view'}
                  placeholder="Nombre de la sucursal"
                  style={{ width: '100%', height: '40px', fontSize: '0.95rem', padding: '0 12px',
                    border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`, borderRadius: '8px',
                    backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  🧍 Departamento <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <InputText 
                  name="departamento"
                  value={formData.departamento}
                  onChange={handleCambios}
                  disabled={mode === 'view'}
                  placeholder="Nombre del departamento"
                  style={{ width: '100%', height: '40px', fontSize: '0.95rem', padding: '0 12px',
                    border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`, borderRadius: '8px',
                    backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff' }}
                />
              </div>
            </div>

            {/* Tercera fila - Observación */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                📝 Observación
              </label>
              <InputTextarea
                name="observacion"
                value={formData.observacion}
                onChange={handleCambios}
                disabled={mode === 'view'}
                rows={4}
                placeholder="Ingrese detalles de la acción realizada con el producto..."
                style={{ width: '100%', fontSize: '0.95rem', padding: '12px',
                  border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`, borderRadius: '8px',
                  backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff', resize: 'vertical' }}
              />
            </div>

            {/* Cuarta fila - Fecha (solo lectura) */}
            {formData.fecha_registro && (
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px', 
                padding: '16px', 
                border: '2px solid #e9ecef'
              }}>
                <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  📅 Fecha de registro
                </label>
                <InputText 
                  value={new Date(formData.fecha_registro).toLocaleString('es-MX')}
                  disabled
                  style={{ width: '100%', height: '40px', fontSize: '0.95rem', padding: '0 12px', 
                    border: '2px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}
                />
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default Bitacora;